import head from 'lodash.head';
import React from 'react';
import get from 'lodash.get';
import { isBefore, isWithinInterval } from 'date-fns';
import { ENV } from './envConstants';

export const NAME = 'Name';
export const NA = 'N/A';
export const ELLIPSES = '...';
export const SPACE = ' ';
export const NO_BREAK_SPACE = '\u00A0';
export const COMPOSITE = 'Composite';
export const LEXILE_TITLE = 'Lexile';
export const ERROR = 'error';
export const SHOULD_VALIDATE_SESSION = 'shouldValidateSession';
export const EMPTY = 'empty';
export const FAQ = 'FAQs';
export const MCLASS_PRODUCT = 'mCLASS Product';
export const STUDENTS_ASSESSED = 'Students Assessed';
export const PROBE_SIDE_PANEL = 'probe-side-panel';
export const BASIC_COMPREHENSION_SIDE_PANEL = 'maze-side-panel';
export const LAST_LOCATION_INDEX = 1;

export const ESPANOL_APPLICATION_NAME = 'espanol';
export const READING_APPLICATION_NAME = 'reading';

export const ENROLLMENT = {
    DISTRICT: 'District',
    SCHOOL: 'School',
    CLASS_AND_GROUP: 'Class/Group',
};

export const LICENSE = {
    D8: 'D8',
    ESPANOL: 'ESPANOL',
    TRC: 'TRC',
    TRC_ESPANOL: 'TRC_ESPANOL',
    VOCABO: 'VOCABO',
    VOCABO_ESPANOL: 'VOCABO_ESPANOL',
    TRO: 'TRO',
    LECTURA: 'LECTURA',
    TEXAS: 'TEXAS',
    NORTH_CAROLINA: 'NORTH_CAROLINA',
    DUAL_LANGUAGE: 'DUAL_LANGUAGE',
    EXPRESS: 'EXPRESS',
    LOUISIANA: 'LOUISIANA',
};

const { NORTH_CAROLINA, TEXAS, D8, LOUISIANA } = LICENSE;

export const LOGO_MAPPER = {
    EXPRESS: 'mclass-express-logo',
    [NORTH_CAROLINA]: 'north-carolina-logo',
    [TEXAS]: 'texas-logo',
    [D8]: 'mclass-logo',
    [LOUISIANA]: 'louisiana-logo',
};

export const PERIOD = {
    BOY: 'BOY',
    MOY: 'MOY',
    EOY: 'EOY',
    SUMMARY: 'SUMMARY',
    PM: 'PM',
    BOY_MOY: 'BOY_MOY',
    MOY_EOY: 'MOY_EOY',
    BOY_EOY: 'BOY_EOY',
    EOY_SUMMER: 'EOY_SUMMER',
    AFTER_SUMMER: 'AFTER_SUMMER',
    AFTER_EOY: 'AFTER_EOY',
    SUMMER: 'SUMMER',
};

export const INSTRUCTION = {
    GROUPS: 'Groups',
    STUDENTS: 'Students',
    ALL_ACTIVITIES: 'All Activities',
};

export const TITLE = {
    BOY_TITLE: 'Beginning of Year',
    MOY_TITLE: 'Middle of Year',
    EOY_TITLE: 'End of Year',
    SUMMARY_TITLE: 'Summary',
    ABOVE_TITLE: 'Above',
    WELL_BELOW_TITLE: 'Well Below',
    BELOW_TITLE: 'Below',
    NOT_PROFICIENT: 'Not Proficient',
    AT_TITLE: 'At',
    BENCHMARK_TITLE: 'Benchmark',
    PROFICIENT_TITLE: 'Proficient',
    CLASS_SUMMARY: 'Class Summary',
    BACK_TO_CLASS_LIST: '< Back to Class List',
    SUMMER_TITLE: 'Summer',
    SUMMER_LDOE_TITLE: 'G3 Summer',
};

export const COLORS = {
    RED: 'red',
    ORANGE: 'orange',
    YELLOW: 'yellow',
    GREEN: 'green',
    BLUE: 'blue',
    GRAY: 'gray',
};

export const INTERP = {
    RED: 'wb-benchmark',
    YELLOW: 'below-benchmark',
    GREEN: 'at-benchmark',
    GREEN_AT_ABOVE: 'at-or-above-benchmark',
    BLUE: 'above-benchmark',
    BLANK: 'blank',
    BELOW: 'below-proficient',
    PROFICIENT: 'proficient',
    PASS: 'Pass',
    NO_PASS: 'No Pass',
};

export const INFO_TIPS = 'infotips';

export const KEY = {
    MEASURE_RESULT_INTERP: 'measure_result.interpretation',
    INTERPRETATION: 'interpretation',
    OVERALL_SCORE: 'overall_score',
    OVERALL_INTERP: 'overall_interp',
    CLIENT_MOD_DATE: 'client_mod_date',
    PERCENTILE: 'percentile',
};

export const DATE_FORMAT = {
    FULL_DATE: 'MM/dd/yyyy',
    DATE_TIME: 'MM/dd/yyyy HH:mm:ss',
};

export const PROBE_DETAIL = {
    BREAK: /\\break\\/g,
    CORRECT: 'correct',
    SELF_CORRECT_SHORTHAND: 'sc',
    WORDS_IN_ROW: 3,
    CORRECT_RESPONSE: 'C',
    NOT_CORRECT_RESPONSE: 'NC',
};

export const GRID = {
    SCROLL_MODE: {
        STATIC: 'static',
        CONTAINER: 'container',
        WINDOW: 'window',
    },
    COL_SPAN_WITH_HEIGHT_STYLE: {
        zIndex: '1',
        top: '40px',
        height: '80px',
    },
    COMMON_HEADER_COL_SPAN_STYLE: { zIndex: '2' },
};

export const CLASS = {
    COMMON_BUTTON_CLASSNAME: 'btn',
    ACTIVE: 'active',
};

export const TRANSITION = {
    APPEAR_AND_ENTER_TIME_OUT_TRANSITION: 500,
    EXIT_TIME_OUT_TRANSITION: 1000,
};

const STUDENT_INFO = 'studentInfo';
const STUDENT_GROUP_NAME = 'groupName';
const STUDENT_NAME = 'name';
export const STUDENT = {
    INFO: STUDENT_INFO,
    ID: `${STUDENT_INFO}.id`,
    FIRST_NAME: `${STUDENT_INFO}.firstName`,
    LAST_NAME: `${STUDENT_INFO}.lastName`,
    GROUP_NAME: STUDENT_GROUP_NAME,
    NAME: STUDENT_NAME,
};

export const LANGUAGE = {
    ENGLISH: 'English',
    SPANISH: 'Spanish',
};

export const APP_LANGUAGE = {
    ENGLISH: 'eng',
    SPANISH: 'spa',
};

export const ICON = {
    DONE: <i className="icon-amplify-done" />,
    CLOSE: <i className="icon-amplify-close" />,
    PAUSED: <i className="icon-amplify-pause" />,
    EDIT: (onEdit = () => {}) => (
        <i role="presentation" onClick={onEdit} className="icon-amplify-admin" />
    ),
};

export const HOW_TO_UNDERSTAND = {
    ASSESSMENT: 'How to understand assessment results',
    PROGRESS: 'How to understand progress monitoring results',
    GOAL: 'How to understand goal setting details',
    GROWTH_ANALYSIS: 'How to understand growth outcomes details',
};

export const GATING = {
    TESTED_OUT: 'tested-out',
    DISCONTINUE: 'discontinue',
};

export const EVENT_LISTENERS = {
    BEFORE_PRINT: 'beforeprint',
    AFTER_PRINT: 'afterprint',
};

export const SECTION_IDS = {
    BENCHMARK_RESULT_TABLE: 'result-table',
    MCLASS_HEADER: 'mclass-header',
    PROGRESS_GRID: 'progress-grid',
    SS_BH: 'benchmark-history',
    TEXT_READING_HISTORY: 'text-reading-history',
    SS_PROGRESS_MEASURE_LIST: 'measure-list',
    FEATURE_REPORT_TABLE: 'feature-report-table',
    WIDGETS_BENCHMARK: 'widgets-benchmark',
};

export const ZOG = {
    GOAL_SETTINGS: 'Goal Setting',
    ZONES_OF_GROWTH: 'Zones of Growth',
};

export const GA = {
    GROWTH_ANALYSIS: 'Growth Outcomes',
};

export const getPreviousPeriod = (currentPeriod, toyPeriods) =>
    toyPeriods[toyPeriods.findIndex(period => period === currentPeriod) - 1] || currentPeriod;

export const getNextPeriod = (currentPeriod, toyPeriods) =>
    currentPeriod === [...toyPeriods].pop()
        ? head(toyPeriods)
        : toyPeriods[toyPeriods.findIndex(period => period === currentPeriod) + 1];

export const getToyTitleByKey = (toyKey, summerPeriodLabel = TITLE.SUMMARY_TITLE) => {
    const { BOY, MOY, EOY, SUMMER, SUMMARY } = PERIOD;
    const { BOY_TITLE, MOY_TITLE, EOY_TITLE, SUMMARY_TITLE } = TITLE;
    const TOYS = [
        { key: BOY, title: BOY_TITLE },
        { key: MOY, title: MOY_TITLE },
        { key: EOY, title: EOY_TITLE },
        { key: SUMMER, title: summerPeriodLabel },
        { key: SUMMARY, title: SUMMARY_TITLE },
    ];

    return get(
        TOYS.find(toy => toy.key === toyKey),
        'title',
        '',
    );
};

export const getPmPeriodByCurrentPeriod = currentPeriod => {
    const { BOY, MOY, BOY_MOY, MOY_EOY, AFTER_EOY } = PERIOD;
    switch (currentPeriod) {
        case BOY:
            return BOY_MOY;
        case MOY:
            return MOY_EOY;
        default:
            return AFTER_EOY;
    }
};

/**
 * Get a period (based on the window dates) for the provided date
 * @param date
 * @param windowDates
 * @returns {string} period in which provided date is located
 */
export const getPeriodByDate = (date, windowDates) => {
    if (isBefore(new Date(date), new Date(windowDates[PERIOD.MOY].startDate))) {
        return PERIOD.BOY;
    }
    if (
        isWithinInterval(new Date(date), {
            start: new Date(windowDates[PERIOD.MOY].startDate),
            end: new Date(windowDates[PERIOD.EOY].startDate),
        })
    ) {
        return PERIOD.MOY;
    }
    return PERIOD.EOY;
};

export const isPopoverFeatureEnabled = chosenDistrictUid => {
    // id's of test accounts
    const isException = [
        '1111901610',
        '1111901654',
        '1111901659',
        '1111924705',
        '1111924835',
        '1111925108',
        '1111925340',
    ].includes(chosenDistrictUid);
    return (window.ENV === ENV.STAGING_ENV || window.ENV === ENV.PRODUCTION_ENV) && !isException;
};

export const resultStateProperties = ['results', 'statuses', 'additionalStatuses'];

export const MUNI_GROUPS = 'muniGroups';

export const DISTRICT_GROUPS = 'districtGroups';
